import {
  Box,
  Heading,
  Text,
  Flex,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import Link from "next/link";
import { FiArrowRight, FiFileText } from "react-icons/fi";

import type { HelpPostProps } from "./Help";

export default function HelpCard({ title, description, slug }: HelpPostProps) {
  const cardBg = useColorModeValue("white", "neutral.800");
  const borderColor = useColorModeValue("neutral.100", "neutral.800");
  const hoverBorderColor = useColorModeValue("brand.200", "brand.600");
  const iconBg = useColorModeValue("orange.50", "orange.900");
  const iconColor = useColorModeValue("brand.500", "brand.300");
  const descriptionColor = useColorModeValue(
    "text.light-secondary",
    "text.dark-muted"
  );
  const arrowColor = useColorModeValue("brand.500", "brand.300");

  return (
    <Link href={`/help/${slug}`} passHref>
      <Box
        as="article"
        w="100%"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        p={6}
        cursor="pointer"
        transition="opacity 0.2s ease, transform 0.2s ease"
        _hover={{
          borderColor: hoverBorderColor,
          transform: "translateY(-2px)",
          shadow: "lg",
        }}
        role="group"
      >
        <Flex direction="column" h="full" minH="140px">
          {/* Icon */}
          <Flex
            w={10}
            h={10}
            bg={iconBg}
            borderRadius="lg"
            align="center"
            justify="center"
            mb={4}
          >
            <Icon
              as={FiFileText}
              boxSize={5}
              color={iconColor}
              aria-hidden="true"
            />
          </Flex>

          {/* Content */}
          <Heading
            as="h3"
            fontWeight={600}
            fontSize="md"
            lineHeight="short"
            mb={2}
            noOfLines={2}
          >
            {title}
          </Heading>

          <Text
            fontSize="sm"
            color={descriptionColor}
            noOfLines={2}
            lineHeight="tall"
            flex={1}
          >
            {description}
          </Text>

          {/* Arrow indicator */}
          <Flex
            align="center"
            mt={4}
            color={arrowColor}
            opacity={0}
            transform="translateX(-4px)"
            transition="border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease"
            _groupHover={{
              opacity: 1,
              transform: "translateX(0)",
            }}
          >
            <Text fontSize="sm" fontWeight={500} mr={1}>
              Read more
            </Text>
            <Icon as={FiArrowRight} boxSize={4} aria-hidden="true" />
          </Flex>
        </Flex>
      </Box>
    </Link>
  );
}
